/**
 * Mock data for Smart Grid Demo
 * Realistic order data structure matching the actual OrderAttributes from the app
 */

export interface MockOrder {
  _id: string;
  serial: string;
  statusId: number;
  statusERP: number;
  subApplication?: {
    code: string;
    name: string;
  };
  trip?: {
    id: number;
  };
  subRoute?: {
    _id: string;
    name: string;
    code?: string;
    externalKeys?: {
      code: string;
    };
    model?: string;
  };
  user?: {
    _id: string;
    user_login: {
      username: string;
    };
  };
  responsibleBusinessPartner?: {
    _id: string;
    name: string;
    personNumber?: string;
    externalKeys?: {
      code: string;
    };
  };
  soldTo: {
    id: string;
    _id: string;
    name: string;
    code: string;
    externalKeys?: {
      code: string;
    };
    address?: {
      city: string;
      country: string;
      street?: string;
      postalCode?: string;
    };
  };
  shipTo: {
    id: string;
    _id: string;
    name: string;
    code: string;
    externalKeys?: {
      code: string;
    };
    address?: {
      city: string;
      country: string;
    };
  };
  shipToType?: string;
  class: {
    _id: string;
    name: string;
    code: string;
    externalKeys?: {
      code: string;
    };
  };
  total: number;
  subTotal: number;
  totalDiscounts: number;
  totalSurcharges: number;
  totalTaxes: number;
  subTotalBeforeTaxes: number;
  createdAt: Date;
  creationDateTime: Date;
  deliveryDateTime?: Date;
  loadDateTime?: Date;
  company: {
    id: string;
    name: string;
  };
  salesOffice?: {
    _id: string;
    name: string;
  };
  warehouse?: {
    _id: string;
    name: string;
    code?: string;
  };
  purchaseOrder?: string;
  note?: string;
  extraStatusERP?: Array<{
    type: string;
    description: string;
  }>;
  detail?: Array<{
    position: number;
    material: {
      _id: string;
      name: string;
      code: string;
    };
    soldQuantity: number;
    unitPrice: number;
    subTotal: number;
    total: number;
  }>;
}

// Helper functions to generate realistic data
function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomElement<T>(array: T[]): T {
  return array[randomInt(0, array.length - 1)];
}

function randomDate(start: Date, end: Date): Date {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
}

// Mock data arrays
const CUSTOMER_NAMES = [
  'Comercial La Popular', 'Super mercado El Ahorro', 'Tienda San Pedro',
  'Minimarket Central', 'Abarrotería González', 'Pulpería María',
  'Supermercado Express', 'Tienda Don José', 'Comercial Los Andes',
  'Minimarket El Sol', 'Abarrotería La Esquina', 'Pulpería La Bendición',
  'Tienda La Esperanza', 'Super El Barato', 'Comercial San Martín',
  'Minimarket La Plaza', 'Tienda Central', 'Pulpería El Buen Precio',
  'Comercial La Victoria', 'Supermercado Familia', 'Tienda La Unión',
  'Minimarket Rápido', 'Abarrotería El Triunfo', 'Pulpería Santa Rosa',
  'Tienda La Paz', 'Super La Economía', 'Comercial Norte',
  'Minimarket La Florida', 'Tienda El Progreso', 'Pulpería La Aurora'
];

const CITIES = [
  'Tegucigalpa', 'San Pedro Sula', 'La Ceiba', 'Choluteca',
  'Comayagua', 'Siguatepeque', 'Danlí', 'El Progreso',
  'Juticalpa', 'Santa Rosa de Copán', 'Tela', 'Puerto Cortés',
  'Choloma', 'Villanueva', 'La Lima', 'Tocoa'
];

const ROUTES = [
  { code: 'RT001', name: 'Ruta Centro', model: 'CENTRO' },
  { code: 'RT002', name: 'Ruta Norte', model: 'NORTE' },
  { code: 'RT003', name: 'Ruta Sur', model: 'SUR' },
  { code: 'RT004', name: 'Ruta Este', model: 'ESTE' },
  { code: 'RT005', name: 'Ruta Oeste', model: 'OESTE' },
  { code: 'RT006', name: 'Ruta Costa', model: 'COSTA' },
  { code: 'RT007', name: 'Ruta Valle', model: 'VALLE' },
];

const USERS = [
  'jperez', 'mrodriguez', 'cgomez', 'alopez',
  'rgarcia', 'lhernandez', 'sdiaz', 'cmartinez'
];

const RESPONSIBLES = [
  { name: 'Juan Pérez', code: 'BP001', personNumber: '0501-1990-12345' },
  { name: 'María Rodríguez', code: 'BP002', personNumber: '0801-1985-67890' },
  { name: 'Carlos Gómez', code: 'BP003', personNumber: '1801-1992-11111' },
  { name: 'Ana López', code: 'BP004', personNumber: '0601-1988-22222' },
];

const DOCUMENT_CLASSES = [
  { code: 'DT001', name: 'Pedido Estándar' },
  { code: 'DT002', name: 'Pedido Urgente' },
  { code: 'DT003', name: 'Pedido Especial' },
  { code: 'DT004', name: 'Pedido Corporativo' },
];

const STATUS_NAMES = {
  0: 'Anulado',
  1: 'Activo',
  6: 'En Progreso',
  7: 'Aprobado',
  8: 'Rechazado',
  18: 'Inactivo',
};

const STATUS_ERP_NAMES = {
  0: 'Pendiente',
  1: 'Enviado',
  2: 'Recibido',
  3: 'Creado',
  4: 'Error',
  5: 'Modificación Pendiente',
  6: 'Error en ERP',
};

const SUB_APPLICATIONS = [
  { code: 'CSMOBILE-BACKOFFICE', name: 'Backoffice' },
  { code: 'CSMOBILE-APP', name: 'Aplicación Móvil' },
  { code: 'CSMOBILE-ERP', name: 'ERP' },
];

const CUSTOMER_TYPES = ['BPT0001', 'BPT9001', 'BPT9002'];

// Generate mock orders
export const MOCK_ORDERS: MockOrder[] = Array.from({ length: 120 }, (_, index) => {
  const statusId = randomElement([1, 1, 1, 1, 1, 6, 7, 0, 8, 18]); // Más pedidos activos
  const statusERP = randomElement([0, 1, 2, 3, 3, 3, 4, 5, 6]);
  const customerId = generateId().substring(0, 24);
  const customerName = randomElement(CUSTOMER_NAMES);
  const customerCode = `CL${String(index + 1).padStart(6, '0')}`;
  const city = randomElement(CITIES);
  const route = randomElement(ROUTES);
  const user = randomElement(USERS);
  const responsible = randomElement(RESPONSIBLES);
  const documentClass = randomElement(DOCUMENT_CLASSES);
  const subApp = randomElement(SUB_APPLICATIONS);
  const hasTrip = Math.random() > 0.3; // 70% tienen viaje
  const customerType = randomElement(CUSTOMER_TYPES);

  // Dates
  const createdAt = randomDate(new Date(2024, 0, 1), new Date());
  const deliveryDate = new Date(createdAt);
  deliveryDate.setDate(deliveryDate.getDate() + randomInt(1, 7));

  // Amounts
  const subTotal = randomInt(1000, 50000);
  const totalDiscounts = Math.round(subTotal * randomInt(0, 15) / 100);
  const totalSurcharges = Math.round(subTotal * randomInt(0, 5) / 100);
  const subTotalBeforeTaxes = subTotal - totalDiscounts + totalSurcharges;
  const totalTaxes = Math.round(subTotalBeforeTaxes * 0.15); // 15% ISV
  const total = subTotalBeforeTaxes + totalTaxes;

  return {
    _id: generateId().substring(0, 24),
    serial: `PED-${new Date().getFullYear()}-${String(index + 1).padStart(6, '0')}`,
    statusId,
    statusERP,
    subApplication: subApp,
    trip: hasTrip ? { id: randomInt(1000, 9999) } : undefined,
    subRoute: {
      _id: generateId().substring(0, 24),
      name: route.name,
      code: route.code,
      externalKeys: { code: route.code },
      model: route.model,
    },
    user: {
      _id: generateId().substring(0, 24),
      user_login: {
        username: user,
      },
    },
    responsibleBusinessPartner: {
      _id: generateId().substring(0, 24),
      name: responsible.name,
      personNumber: responsible.personNumber,
      externalKeys: { code: responsible.code },
    },
    soldTo: {
      id: customerId,
      _id: customerId,
      name: customerName,
      code: customerCode,
      externalKeys: { code: customerCode },
      address: {
        city: city,
        country: 'Honduras',
        street: `Calle Principal ${randomInt(1, 100)}`,
        postalCode: `${randomInt(10000, 99999)}`,
      },
    },
    shipTo: {
      id: customerId, // Mismo que soldTo en muchos casos
      _id: customerId,
      name: Math.random() > 0.7 ? randomElement(CUSTOMER_NAMES) : customerName,
      code: customerCode,
      externalKeys: { code: customerCode },
      address: {
        city: Math.random() > 0.7 ? randomElement(CITIES) : city,
        country: 'Honduras',
      },
    },
    shipToType: customerType,
    class: {
      _id: generateId().substring(0, 24),
      name: documentClass.name,
      code: documentClass.code,
      externalKeys: { code: documentClass.code },
    },
    total,
    subTotal,
    totalDiscounts,
    totalSurcharges,
    totalTaxes,
    subTotalBeforeTaxes,
    createdAt,
    creationDateTime: createdAt,
    deliveryDateTime: deliveryDate,
    loadDateTime: new Date(createdAt.getTime() - 3600000), // 1 hora antes
    company: {
      id: 'COMP001',
      name: 'Albatros Virtual Ecosystem',
    },
    salesOffice: {
      _id: generateId().substring(0, 24),
      name: `Oficina ${city}`,
    },
    warehouse: {
      _id: generateId().substring(0, 24),
      name: `Almacén ${city}`,
      code: `ALM${String(randomInt(1, 20)).padStart(3, '0')}`,
    },
    purchaseOrder: Math.random() > 0.5 ? `OC-${randomInt(1000, 9999)}` : undefined,
    note: Math.random() > 0.7 ? 'Entrega urgente requerida' : undefined,
    extraStatusERP: [
      { type: 'GLOBAL', description: STATUS_ERP_NAMES[statusERP] || 'Desconocido' },
      { type: 'DELIVERY', description: randomElement(['Pendiente', 'En Ruta', 'Entregado']) },
      { type: 'INVOICE', description: randomElement(['Sin Facturar', 'Facturado Parcial', 'Facturado']) },
    ],
    detail: Array.from({ length: randomInt(1, 5) }, (_, detailIndex) => ({
      position: detailIndex + 1,
      material: {
        _id: generateId().substring(0, 24),
        name: `Producto ${randomInt(100, 999)}`,
        code: `MAT${String(randomInt(1, 500)).padStart(6, '0')}`,
      },
      soldQuantity: randomInt(1, 100),
      unitPrice: randomInt(50, 1000),
      subTotal: randomInt(100, 5000),
      total: randomInt(100, 6000),
    })),
  };
});

// Helper function to get orders by status
export function getOrdersByStatus(statusId: number): MockOrder[] {
  return MOCK_ORDERS.filter(order => order.statusId === statusId);
}

// Helper function to get orders by date range
export function getOrdersByDateRange(startDate: Date, endDate: Date): MockOrder[] {
  return MOCK_ORDERS.filter(order =>
    order.createdAt >= startDate && order.createdAt <= endDate
  );
}

// Helper function to get recent orders
export function getRecentOrders(count: number = 10): MockOrder[] {
  return [...MOCK_ORDERS]
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    .slice(0, count);
}

// Export count
export const MOCK_ORDERS_COUNT = MOCK_ORDERS.length;
